// Objectionable-text filter — pure, shared by the web app (inline sign-up UX) and
// the Cloud Functions (authoritative enforcement). It screens the only user-set,
// publicly-visible free text in the app — display names, @usernames and
// mini-league names — for slurs and strong profanity, satisfying App Store
// Guideline 1.2(a) ("a method for filtering objectionable material").
//
// It is a deliberately conservative blocklist matched against a NORMALISED form of
// the input (lower-cased, accents stripped, common leetspeak folded, elongation
// collapsed) so trivial obfuscations ("f@g", "n1gg3r", "fuuuck") don't slip past.
// No blocklist is perfect, but this is a real, testable gate — and because the
// server runs the identical check, a clean client can never be the only guard.
/** Common character substitutions folded before matching (leetspeak → letters). */
const LEET = {
    '0': 'o',
    '1': 'i',
    '3': 'e',
    '4': 'a',
    '5': 's',
    '7': 't',
    '8': 'b',
    '9': 'g',
    '@': 'a',
    '$': 's',
    '!': 'i',
    '|': 'i'
};
/**
 * Reduce a string to the canonical form the blocklist is matched against:
 * lower-case, strip diacritics, fold leetspeak, drop everything that isn't a
 * letter/digit, and collapse 3+ repeats of a character to one (so "fuuuuck"
 * normalises to "fuck"). Pure and deterministic.
 */
export function normalizeForModeration(input) {
    const lowered = input.toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
    let folded = '';
    for (const ch of lowered)
        folded += LEET[ch] ?? ch;
    return folded.replace(/[^a-z0-9]/g, '').replace(/(.)\1{2,}/g, '$1');
}
/**
 * Slurs / strong profanity matched even when embedded inside other characters
 * (e.g. "xx_faggot_xx", "fuckface", "fuck123"). EVERY entry here has been vetted to
 * have no realistic innocent embedding, because substring matching is what triggers
 * the "Scunthorpe problem". Deliberately conservative: ambiguous terms that DO occur
 * inside ordinary words ("cunt"→Scunthorpe, "rapist"→therapist, "shit"→mishit,
 * "kike"→the name Kike) live in WORD_BANNED below and are matched per-token instead.
 */
const SUBSTRING_BANNED = [
    'nigger',
    'faggot',
    'motherfuck',
    'fuck',
    'wetback',
    'childporn',
    'pedophil',
    'paedophil',
    'molest',
    // Danish
    'fisse',
    'perker',
    'luder'
];
/**
 * Whole-word blocklist (matched against each token of the input, and against the
 * whole stripped string for single-word handles). Holds the milder/ambiguous terms
 * that must NOT be substring-matched, so ordinary words stay allowed:
 * "assassin"/"Cassidy" (assassin≠ass-slur), "Scunthorpe" (cunt), "therapist" (rapist),
 * "mishit"/"shiitake" (shit), "Dickson"/"Hancock" (dick/cock), "Kike"/"Kikelomo" (kike).
 */
const WORD_BANNED = [
    // racial / ethnic
    'nigga',
    'spic',
    'spick',
    'gook',
    'beaner',
    'coon',
    'negro',
    'paki',
    'chink',
    'kike',
    // homophobic / transphobic
    'fag',
    'fags',
    'dyke',
    'tranny',
    'faggots',
    // ableist
    'retard',
    'retarded',
    'spastic',
    'mong',
    // sexual / strong profanity
    'fuk',
    'cock',
    'dick',
    'dickhead',
    'pussy',
    'penis',
    'cum',
    'jizz',
    'blowjob',
    'handjob',
    'whore',
    'slut',
    'bitch',
    'bastard',
    'wanker',
    'asshole',
    'arsehole',
    'cunt',
    'shit',
    'shithead',
    'bullshit',
    'dumbass',
    'jackass',
    'twat',
    'boner',
    'rape',
    'rapist',
    'pedo',
    'nazi',
    'hitler',
    'kkk',
    // Danish
    'kusse',
    'svans',
    'neger',
    'spasser',
    'mongol',
    'pikhoved',
    'pikslikker',
    'kraftedme'
];
const WORD_SET = new Set(WORD_BANNED.map(normalizeForModeration));
const SUBSTRINGS = SUBSTRING_BANNED.map(normalizeForModeration).filter(Boolean);
/**
 * Does the text contain a banned slur or strong profanity? Used to reject
 * objectionable display names, usernames and mini-league names. Conservative by
 * design: it should catch obvious abuse without blocking ordinary names.
 */
export function containsProfanity(input) {
    if (!input)
        return false;
    const stripped = normalizeForModeration(input);
    if (!stripped)
        return false;
    for (const bad of SUBSTRINGS) {
        if (stripped.includes(bad))
            return true;
    }
    // Token check: split the original on anything that isn't a letter/number so
    // "you suck fag" → ["you", "suck", "fag"], then normalise each token. Also test
    // the whole stripped string for single-word handles like "fuck123".
    const tokens = input.split(/[^\p{L}\p{N}]+/u).map(normalizeForModeration);
    tokens.push(stripped);
    for (const token of tokens) {
        if (token && WORD_SET.has(token))
            return true;
    }
    return false;
}
/**
 * A user-facing validation message if the text is objectionable, else null.
 * `noun` tailors the copy ("name", "username", "league name").
 */
export function profanityError(input, noun = 'name') {
    return containsProfanity(input) ? `Please choose a different ${noun}.` : null;
}
//# sourceMappingURL=moderation.js.map